import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import Modal from '../../components/Modal';

/**
 * Generic multi-step shell rendered inside a Modal.
 *
 * Each step is { id, label, render(), isComplete() }. `render` returns the step
 * body; `isComplete` gates Next. The final step's button runs onComplete, which
 * returns a promise — while it is pending the wizard is locked and the button
 * shows busyLabel.
 *
 * Steps are declared by the caller, so both wizards share navigation, progress
 * and submit handling without sharing their content.
 */
function WizardShell({ title, steps, submitLabel, busyLabel = 'Working...', error, submitting, onComplete, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const canAdvance = step.isComplete ? step.isComplete() : true;

  const back = () => setStepIndex((i) => Math.max(0, i - 1));
  const next = () => setStepIndex((i) => Math.min(steps.length - 1, i + 1));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canAdvance || submitting) return;
    if (isLastStep) onComplete();
    else next();
  };

  return (
    <Modal title={title} onClose={onClose}>
      {/* Markers carry the position, and only the step you are on is named.
          Six steps' worth of labels across a phone-width modal truncated to
          "Na... To... Th...", which is a row of six things none of which can be
          read - the rail was reporting progress in a form that had stopped being
          words. Each label stays in the markup for anything reading the page
          aloud; the caption underneath is where a person reads it. */}
      <div className="wizard-progress">
        <ol className="wizard-steps">
          {steps.map((s, i) => (
            <li
              key={s.id}
              className={
                i === stepIndex ? 'wizard-step current'
                  : i < stepIndex ? 'wizard-step done'
                    : 'wizard-step'
              }
              aria-current={i === stepIndex ? 'step' : undefined}
            >
              <span className="wizard-step-marker">{i < stepIndex ? <Check size={13} /> : i + 1}</span>
              <span className="wizard-step-label">{s.label}</span>
            </li>
          ))}
        </ol>

        <p className="wizard-progress-caption">
          <span className="wizard-progress-count">Step {stepIndex + 1} of {steps.length}</span>
          <span className="wizard-progress-title">{step.label}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {step.render()}

        {error && <div className="error-message">{error}</div>}

        <div className="wizard-actions">
          {stepIndex > 0 && (
            <button type="button" className="secondary-btn" onClick={back} disabled={submitting}>
              <ChevronLeft size={18} />
              Back
            </button>
          )}
          <button type="submit" className="primary-btn wizard-next" disabled={!canAdvance || submitting}>
            {submitting ? busyLabel : isLastStep ? submitLabel : 'Next'}
            {!submitting && !isLastStep && <ChevronRight size={18} />}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default WizardShell;
